import React, { useEffect, useMemo, useRef, useState } from "react";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";

import { DISPENSARY_MEDICAL_DISCLAIMER } from "constants/disclaimers";
import { Action, Category } from "constants/events";
import { Dispensary } from "custom-types/Dispensary";
import {
  MenuItem,
  MenuItemVariant,
  MenuItemVariantOffer,
} from "custom-types/MenuItem";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import { setUserMenuPreference } from "redux/action-creators/user";
import { getCoordinates } from "redux/selectors/location";
import { getUserPrefersMedical } from "redux/selectors/user";
import { AppThunkDispatch } from "redux/store";
import buildDispensaryGoogleMapsUrl from "utils/dispensary/buildDispensaryGoogleMapsUrl";
import getDistanceFromDispensary from "utils/dispensary/getDistanceFromDispensary";
import isDispensaryAcceptingOrders from "utils/dispensary/isDispensaryAcceptingOrders";
import isDispensaryDualLicense from "utils/dispensary/isDispensaryDualLicense";
import { MenuType } from "utils/menuTypeUtils";
import { trackEvent, trackMenuEvent } from "utils/trackEvent";
import { trackUserHistory } from "utils/userHistory";

import Button from "components/botanic/Button";
import QuantitySelector from "components/botanic/QuantitySelector/QuantitySelector";
import CategoryCarousel from "components/Dispensary/CategoryCarousel";
import OpenStatus from "components/Dispensary/OpenStatus";
import StrainReviews from "components/Dispensary/StrainReviews";
import VariantSelect from "components/Dispensary/VariantSelect";
import Image from "components/Image";
import MedicalDisclaimers from "components/MedicalDisclaimers";
import { AddToBagButton } from "components/Shared/MenuItemCarousel/AddToBagButton";
import MenuTypeSelection from "components/Shared/MenuTypeSelection";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";

import ReportProblemMailer from "../../ReportProblemMailer/ReportProblemMailer";
import { Offers, OffersModal } from "../Offers";
import SimilarMenuItemsCarousel from "../SimilarMenuItemsCarousel";
import Description from "./Description";
import MenuItemHeader from "./MenuItemHeader";

const LeaflyNugKnowledge = dynamic(
  () => import("components/Dispensary/LeaflyNugKnowledge"),
);

const ProductComparison = dynamic(() => import("../ProductComparison"));

const getInitialVariant = (
  variantQuery: string | string[] | undefined,
  item: MenuItem,
) => {
  if (variantQuery) {
    const variant = item.variants.find(
      (variant) => variant.id === parseInt(variantQuery as string, 10),
    );

    if (variant) {
      return variant;
    }
  }

  return item.variants.reduce((lowest, variant) =>
    variant.price < lowest.price ? variant : lowest,
  );
};

const MenuItemPage: React.FC<{
  dispensary: Dispensary;
  menuItem: MenuItem;
  similarMenuItems: MenuItem[];
}> = ({ dispensary, menuItem, similarMenuItems }) => {
  const { query, replace } = useRouter();
  const countryCode = useDomainCountryCode();
  const coordinates = useSelector(getCoordinates);

  const initialVariantId = query && query.variant;
  const [offerModalVisible, setOfferModalVisible] = useState(false);
  const acceptsOrders = isDispensaryAcceptingOrders(dispensary);
  const googleMapsUrl = buildDispensaryGoogleMapsUrl(dispensary);
  const localizedDistance = getDistanceFromDispensary(
    countryCode,
    dispensary,
    coordinates,
  );
  const userPrefersMedical = !!useSelector(getUserPrefersMedical);

  const isDualLicense = isDispensaryDualLicense(dispensary.tags);

  const dispatch = useDispatch<AppThunkDispatch>();

  const inStoreOnly = !acceptsOrders || !menuItem.pickupEnabled;

  const buttonRef = useRef(null);

  const [variantSelected, setVariantSelected] = useState(
    getInitialVariant(initialVariantId, menuItem),
  );
  const [quantity, setQuantity] = useState(1);

  const { publishEvent } = useEventTracker();

  const showLeaflyNugKnowledge = !isEmpty(menuItem.strain);

  const variantsToShow = useMemo(() => {
    const isDualLicense = isDispensaryDualLicense(menuItem?.dispensary?.tags);
    return isDualLicense
      ? menuItem?.variants?.filter(
          (variant: MenuItemVariant) => variant.medical === userPrefersMedical,
        )
      : menuItem?.variants || [];
  }, [JSON.stringify(menuItem), userPrefersMedical]);

  const variantOffers = useMemo(() => {
    const offers = variantsToShow.reduce(
      (offersArray: MenuItemVariantOffer[], variant: MenuItemVariant) => {
        return [...offersArray, ...variant.offers];
      },
      [],
    );
    // * Need to unique by offer id, in case the same BOGO is applied
    // * to multiple variants. In most cases where this selector is used,
    // * we only want to show an Offer once.
    return uniqBy(offers, (offer: MenuItemVariantOffer) => offer.id);
  }, [JSON.stringify(variantsToShow)]);

  const isOrderable =
    acceptsOrders && menuItem.pickupEnabled && variantsToShow.length > 0;

  const handleSelectVariant = (variant: MenuItemVariant) => {
    setVariantSelected(variant);
    publishEvent({
      action: Action.click,
      category: Category.menuItemDetailVariants,
      dispensaryId: dispensary.id,
      label: `${variant.cartQuantity} ${variant.cartUnit}${
        dispensary.inStoreCartEnabled ? " - shop every menu" : ""
      }`,
      menuItemId: menuItem.id,
    });
  };

  const handleSetOfferModal = (isVisible: boolean) => {
    setOfferModalVisible(isVisible);
    if (isVisible) {
      trackEvent(Category.menuDetails, Action.click, "see all");
    }
  };

  const trackDirectionsClick = () => {
    trackMenuEvent("Pdp", "Directions", { menuItem });
  };

  const sharedEventProps = {
    category: Category.dispensaryMenuPdp,
    dispensaryId: dispensary.id,
  };

  const trackDecrementClickEvent = () => {
    const label = dispensary.inStoreCartEnabled
      ? "qty decrease - shop every menu"
      : "qty decrease";

    publishEvent({
      action: Action.click,
      label,
      ...sharedEventProps,
    });
  };

  const trackIncrementClickEvent = () => {
    const label = dispensary.inStoreCartEnabled
      ? "qty increase - shop every menu"
      : "qty increase";

    publishEvent({
      action: Action.click,
      label,
      ...sharedEventProps,
    });
  };

  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      trackUserHistory({
        attributes: {
          dispensarySlug: dispensary?.slug || "",
        },
        resourceId: menuItem.menuItemId,
        type: "menu-item",
      });

      isMounted.current = true;
    }

    const initialVariant = getInitialVariant(initialVariantId, menuItem);
    setVariantSelected(initialVariant);
    if (isDualLicense && initialVariant.medical !== userPrefersMedical) {
      dispatch(
        setUserMenuPreference(
          initialVariant.medical ? MenuType.Med : MenuType.Rec,
        ),
      );
    }
  }, [initialVariantId, menuItem]);

  const handleMenuTypeChange = (value: MenuType.Med | MenuType.Rec) => {
    const newVariant =
      menuItem.variants.find(
        (variant) => variant.medical && value === MenuType.Med,
      ) || menuItem.variants[0];

    setVariantSelected(newVariant);

    replace({ query: { ...query, variant: newVariant.id } });
  };

  return (
    <>
      <div className="bg-white pt-xl">
        <div className="container">
          <MenuTypeSelection
            dispensaryId={dispensary.id}
            className="mb-md text-right"
            isDualLicense={isDualLicense}
            onMenuTypeChange={handleMenuTypeChange}
            showSelectInput={true}
          />

          <div data-testid="menu-item-detail" className="row">
            {/* Mobile header */}
            <div className="col md:hidden mb-lg">
              {inStoreOnly && !dispensary.inStoreCartEnabled && (
                <div className="font-bold text-sm mb-md">
                  Only available for purchase in-store
                </div>
              )}
              <MenuItemHeader menuItem={menuItem} />
            </div>

            <div data-testid="menu-item-image" className="col md:col-7">
              <div className="relative top-0 left-0">
                <div className="row h-full md:pb-md items-center justify-center relative top-0 left-0">
                  <div className="col md:col-3/4 flex justify-center">
                    <Image
                      className="bg-white"
                      sizes={[450, null, 200, 350, null, 350]}
                      alt={menuItem.name}
                      src={menuItem.imageUrl || menuItem.thumbnailUrl}
                      disableLazyLoad
                      preload
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col md:col-5">
              {/* Tablet+ header */}
              <div className="hidden md:inline-block">
                {inStoreOnly && !dispensary.inStoreCartEnabled && (
                  <div className="font-bold text-sm mb-md">
                    Only available for purchase in-store
                  </div>
                )}
                <MenuItemHeader menuItem={menuItem} />
              </div>

              <div data-testid="menu-item-buybox" className="mt-xl pb-xl">
                <div className="font-bold text-sm mb-sm">
                  {inStoreOnly && !dispensary.inStoreCartEnabled
                    ? "Only available for purchase in-store"
                    : variantsToShow.length > 1
                      ? "Choose your amount"
                      : "Amount"}
                </div>
                <VariantSelect
                  allowSelections={
                    (acceptsOrders && menuItem.pickupEnabled) ||
                    dispensary.inStoreCartEnabled
                  }
                  variantSelected={variantSelected}
                  variants={variantsToShow}
                  selectVariant={handleSelectVariant}
                  countryCode={countryCode}
                  inStoreCart={dispensary.inStoreCartEnabled}
                />
                <Offers
                  buttonRef={buttonRef}
                  dispensarySlug={dispensary.slug}
                  offers={variantOffers}
                  setModalIsVisible={handleSetOfferModal}
                />
                {offerModalVisible && (
                  <OffersModal
                    buttonRef={buttonRef}
                    dispensarySlug={dispensary.slug}
                    offers={variantOffers}
                    setModalIsVisible={handleSetOfferModal}
                  />
                )}
                {(isOrderable || dispensary.inStoreCartEnabled) && (
                  <>
                    <QuantitySelector
                      className="pt-4"
                      label="Quantity"
                      onChange={setQuantity}
                      trackDecrementClickEvent={trackDecrementClickEvent}
                      trackIncrementClickEvent={trackIncrementClickEvent}
                      value={quantity}
                    />
                    <AddToBagButton
                      className="my-lg"
                      defaultVariantId={variantSelected.id}
                      iconDimensions="large"
                      immediatelyAddToBag={true}
                      menuItem={menuItem}
                      quantity={quantity || 1}
                    />
                  </>
                )}
                {googleMapsUrl && (
                  <Button
                    className="bg-green-20 rounded text-left text-default py-sm px-md font-bold text-xs"
                    onClick={trackDirectionsClick}
                    target="_blank"
                    rel="noopener noreferrer"
                    prefetch={false}
                    href={googleMapsUrl}
                  >
                    <div>{dispensary?.name}</div>
                    <div>
                      <OpenStatus
                        dispensary={dispensary}
                        className="inline-block"
                      />
                      {!!localizedDistance.full && (
                        <>
                          <span className="mx-md">|</span>
                          <span className="text-green">
                            {localizedDistance.full}
                          </span>
                        </>
                      )}
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-light-grey">
        <div className="container py-xxl">
          <Description menuItem={menuItem} />
        </div>
      </div>
      {showLeaflyNugKnowledge && (
        <div
          className="px-lg  bg-leafly-white"
          data-testid="leafly-nug-knowledge"
        >
          <div className="py-lg grid grid-cols-[minmax(0px,_828px)] items-center justify-center gap-md">
            <LeaflyNugKnowledge
              strainSlug={menuItem.strain?.slug}
              menuItemId={menuItem.id}
            />
          </div>
        </div>
      )}
      {menuItem.productCategory.toLowerCase() === "flower" && (
        <ProductComparison menuItem={menuItem} className="mt-xl" />
      )}
      <div className="bg-leafly-white">
        <div className="container py-xl">
          <SimilarMenuItemsCarousel
            className="mb-xxl"
            similarMenuItems={similarMenuItems}
          />
          {menuItem.strain?.id && (
            <StrainReviews strain={menuItem.strain} className="mb-xxl" />
          )}
          <RecentlyViewedMenuItems
            className="mb-xxl"
            attributes={{ dispensarySlug: dispensary.slug }}
            isDispensaryDualLicense={isDualLicense}
          />
          <CategoryCarousel
            dispensaryRetailType={dispensary.retailType}
            dispensarySlug={dispensary.slug}
          />
          <ReportProblemMailer wrapperClasses="pt-section" />
          <div className="pt-xl">
            <MedicalDisclaimers usDisclaimer={DISPENSARY_MEDICAL_DISCLAIMER} />
          </div>
        </div>
      </div>
    </>
  );
};

export default MenuItemPage;
